import { Box, Spinner, Text } from "@chakra-ui/react";
import { Node, NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, mergeAttributes } from "@tiptap/react";
import { useEffect, useState } from "react";
import api from "../../services/api";
import { crm } from "../../theme";

// Anexo citado no texto: o HTML guarda só data-attachment-id (o download exige o token, então <img src> não serve).
export const ATTACHMENT_ATTR = "data-attachment-id";

// Object URL compartilhado por anexo, liberado quando ninguém mais mostra a imagem
const cache = new Map<string, { url: Promise<string>; users: number }>();

export const acquire = (id: string) => {
  let entry = cache.get(id);
  if (!entry) {
    const url = api.get(`/api/intranet/attachments/${id}`, { responseType: "blob" }).then((response) => URL.createObjectURL(response.data));
    entry = { url, users: 0 };
    cache.set(id, entry);
    const created = entry;
    // Só apaga a própria entrada (outra pode ter sido criada depois por um acquire novo)
    url.catch(() => { if (cache.get(id) === created) cache.delete(id); });
  }
  entry.users++;
  return entry.url;
};

export const release = (id: string) => {
  const entry = cache.get(id);
  if (!entry || --entry.users > 0) return;
  cache.delete(id);
  entry.url.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);
};

export const useAttachmentUrl = (id?: string | null) => {
  const [state, setState] = useState<{ url?: string; failed?: boolean }>({});
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setState({});
    acquire(id).then((url) => alive && setState({ url })).catch(() => alive && setState({ failed: true }));
    return () => {
      alive = false;
      release(id);
    };
  }, [id]);
  return state;
};

export const AttachmentImageView = ({ id }: { id?: string | null }) => {
  const { url, failed } = useAttachmentUrl(id);
  if (failed) return <Text as="span" fontSize="xs" color={crm.textFaint}>[imagem indisponível]</Text>;
  if (!url) return <Box as="span" display="inline-flex" alignItems="center" gap="6px" fontSize="xs" color={crm.textFaint}><Spinner size="xs" />carregando imagem…</Box>;
  return <img src={url} alt="" style={{ display: "block", maxWidth: "100%", margin: "8px 0", borderRadius: 6, border: `1px solid ${crm.border}` }} />;
};

const EditorImage = ({ node, selected }: NodeViewProps) => (
  <NodeViewWrapper as="span" style={{ display: "block", outline: selected ? `2px solid ${crm.brand}` : undefined, borderRadius: 6 }}>
    <AttachmentImageView id={node.attrs.attachmentId} />
  </NodeViewWrapper>
);

export const AttachmentImage = Node.create({
  name: "attachmentImage",
  inline: true,
  group: "inline",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      attachmentId: {
        default: null,
        parseHTML: (element) => element.getAttribute(ATTACHMENT_ATTR),
        renderHTML: (attributes) => ({ [ATTACHMENT_ATTR]: attributes.attachmentId }),
      },
    };
  },
  parseHTML() {
    return [{ tag: `img[${ATTACHMENT_ATTR}]` }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(EditorImage);
  },
});
